<template>
  <v-container class="px-6 pt-0 pb-3 report-container">
    <v-dialog v-model="dialogCreate" width="2000">
      <SweetSpotSetup
        v-if="dialogCreate || componentSubmissionMode === 'create'"
        :report_id="report_id"
        :shared_audience="sharedAudience"
        :audience_managed="true"
        v-on:close="dialogCreate = false"
        v-on:component_submission_started="handleComponentSubmissionStarted"
        v-on:component_setup_completed="component_setup_completed"
        v-on:component_setup_failed="handleComponentSetupFailed"
      ></SweetSpotSetup>
    </v-dialog>

    <v-dialog v-model="dialogComponentEdit" width="2000">
      <SweetSpotSetup
        v-if="dialogComponentEdit || componentSubmissionMode === 'edit'"
        mode="edit"
        :report_id="report_id"
        :shared_audience="sharedAudience"
        :audience_managed="true"
        :edit_component_data="componentToEdit"
        v-on:close="closeComponentEditDialog"
        v-on:component_submission_started="handleComponentSubmissionStarted"
        v-on:component_edit_completed="component_edit_completed"
        v-on:component_setup_failed="handleComponentSetupFailed"
      ></SweetSpotSetup>
    </v-dialog>

    <v-dialog v-model="dialogAudience" width="2000" :persistent="audienceSaving">
      <div v-if="audienceError" class="pa-3 white"><v-alert type="error" dense class="mb-0">{{ audienceError }}</v-alert></div>
      <SweetSpotSetup
        v-if="dialogAudience || audienceSaving"
        :report_id="report_id"
        :audience_only="true"
        :shared_audience="sharedAudience"
        :audience_saving="audienceSaving"
        :audience_components="sweetspot_data"
        @close="closeAudienceDialog"
        @audience_saved="saveSharedAudience"
      ></SweetSpotSetup>
    </v-dialog>

    <v-dialog v-model="dialogOptimize" width="2000">
      <SweetSpotOptimizeCard
        :optimization="optimization"
        :optimization_error="optimization_error"
        :optimization_metric_options="optimizationMetricOptions"
        :optimization_type_options="optimizationTypeOptions"
        :optimization_offset_levels="optimizationOffsetLevels"
        :can_run_optimization="canRunOptimization"
        :get_optimization_offset_values="getOptimizationOffsetValues"
        v-on:close="dialogOptimize = false"
        v-on:update_optimization_field="updateOptimizationField"
        v-on:add_offset="addOptimizationOffset"
        v-on:remove_offset="removeOptimizationOffset"
        v-on:run_optimization="runOptimization"
      ></SweetSpotOptimizeCard>
    </v-dialog>
  
    <v-dialog
      v-model="dialogEdit"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          <span class="dialog_title">Edit Data</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row class="ma-0 pa-0">
              <v-col cols="12">
                <v-text-field
                  v-model="curr_row_edit_val['spend']"
                  label="Spend"
                  prefix="$"
                  type="number"
                  inputmode="decimal"
                  :disabled="is_edit_field_disabled('spend')"
                  @input="lock_edit_field('spend')"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="ma-0 pa-0">
              <v-col cols="12">
                <v-text-field
                  v-model="curr_row_edit_val['impressions']"
                  label="Impressions"
                  type="number"
                  inputmode="decimal"
                  :disabled="is_edit_field_disabled('impressions')"
                  @input="lock_edit_field('impressions')"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="ma-0 pa-0">
              <v-col cols="12">
                <v-text-field
                  v-model="curr_row_edit_val['grps']"
                  label="GRPs"
                  type="number"
                  inputmode="decimal"
                  :disabled="is_edit_field_disabled('grps')"
                  @input="lock_edit_field('grps')"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="ma-0 pa-0">
              <v-col cols="12">
                <v-text-field
                  v-model="curr_row_edit_val['daily_grps']"
                  label="Daily GRPs"
                  type="number"
                  inputmode="decimal"
                  :disabled="is_edit_field_disabled('daily_grps')"
                  @input="lock_edit_field('daily_grps')"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-row class="ma-0 pa-0">
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn class="action_text" outlined color="black" @click="dialogEdit = false;">Cancel</v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn class="action_text" outlined color="black" @click="editTableRowConfirm()">Save Changes</v-btn>
            </v-col>
          </v-row>                
        </v-card-actions>
      </v-card>
    </v-dialog>
  
    <v-row class="ma-0 pa-0 full-size-row">
      <v-col cols="12" class="report-toolbar">
        <div class="report-toolbar__chips">
          <v-chip
            label
            class="data_breakdown_chip"
            :outlined="selected_page != 'portfolio'"
            @click="selected_page = 'portfolio'"
            color="black"
            >Portfolio</v-chip
          >
          <v-chip
            label
            class="data_breakdown_chip"
            :disabled="sweetspot_data.length == 0"
            :outlined="selected_page != 'performance'"
            @click="selected_page = 'performance'"
            color="black"
            >Performance
          </v-chip>
          <v-chip
            label
            class="data_breakdown_chip"
            :disabled="sweetspot_data.length == 0"
            :outlined="selected_page != 'inventory'"
            @click="selected_page = 'inventory'"
            color="black"
            >Inventory
          </v-chip>
        </div>
        <v-btn
          v-if="selected_page === 'inventory' && !inventory_loading"
          small
          outlined
          :aria-expanded="String(inventory_table_visible)"
          aria-controls="sweetspot-inventory-table"
          @click="inventory_table_visible = !inventory_table_visible"
        >
          <v-icon left small>{{ inventory_table_visible ? 'mdi-table-off' : 'mdi-table' }}</v-icon>
          {{ inventory_table_visible ? 'Hide Table' : 'Show Table' }}
        </v-btn>
        <div class="report-toolbar__actions" v-if="selected_page != 'inventory'">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-switch
                v-model="omni_channel_enabled"
                class="omni-channel-toggle ma-0 pa-0 px-6"
                color="black"
                dense
                :disabled="omni_channel_toggle_loading || page_loading"
                hide-details
                inset
                v-on="on"
                @change="handle_omni_channel_toggle">
                <template v-slot:label>
                  <span class="omni-channel-toggle__label">Omni Channel Reach</span>
                  <v-icon class="ma-0 pa-0 pl-2">mdi-chart-multiple</v-icon>
                  <!-- <v-icon class="ma-0 pa-0 pl-2">mdi-chart-bell-curve-cumulative</v-icon> -->
                </template>
              </v-switch>
            </template>
            <TooltipDescription :title="omni_channel_enabled ? 'Hide Omni Channel' : 'Show Omni Channel'"></TooltipDescription>
          </v-tooltip>
          <v-btn
            outlined
            color="black"
            class="optimize-button"
            :disabled="page_loading || sweetspot_data.length === 0"
            @click="openOptimizeDialog"
          >
            Optimize
          </v-btn>
          <!-- <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span class="save-tooltip-activator" v-on="on">
                <v-btn
                  icon
                  large
                  :disabled="!can_save_report || page_loading"
                  @click="save_portfolio_changes"
                  height="40px"
                  width="40px"
                >
                  <v-icon color="black">mdi-content-save-outline</v-icon>
                </v-btn>
              </span>
            </template>
            <TooltipDescription :title="saveTooltipTitle"></TooltipDescription>
          </v-tooltip> -->
          <v-menu
            v-model="omni_channel_level_menu"
            offset-y
            :close-on-content-click="false"
            max-width="260"
            v-if="selected_page == 'portfolio'"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="black"
                class="omni-channel-level-button"
                :disabled="omni_channel_toggle_loading || page_loading"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left small>mdi-sort</v-icon>
                <span class="omni-channel-level-button__label">Level Order</span>
              </v-btn>
            </template>
            <v-card class="omni-channel-level-card">
              <v-card-title class="omni-channel-level-card__title">
                Level Order
              </v-card-title>
              <v-card-text class="omni-channel-level-card__content">
                <div
                  v-for="(level, index) in omni_channel_level_order"
                  :key="level"
                  class="omni-channel-level-row"
                  :class="{ 'omni-channel-level-row--locked': index === 0 }"
                >
                  <v-chip class="omni-channel-level-chip" label>
                    <v-avatar
                      class="black white--text"
                      left
                      v-text="index + 1"
                    ></v-avatar>
                    {{ formatLevelLabel(level) }}
                  </v-chip>
                  <div class="omni-channel-level-row__actions">
                    <v-tooltip top v-if="index === 0">
                      <template v-slot:activator="{ on }">
                        <v-icon small color="#6B7280" v-on="on">mdi-lock-outline</v-icon>
                      </template>
                      <TooltipDescription title="Market is always first"></TooltipDescription>
                    </v-tooltip>
                    <template v-else>
                      <v-btn
                        icon
                        small
                        :disabled="index === 1 || omni_channel_toggle_loading || page_loading"
                        @click="moveOmniChannelLevel(index, -1)"
                      >
                        <v-icon small>mdi-arrow-up</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        :disabled="index === omni_channel_level_order.length - 1 || omni_channel_toggle_loading || page_loading"
                        @click="moveOmniChannelLevel(index, 1)"
                      >
                        <v-icon small>mdi-arrow-down</v-icon>
                      </v-btn>
                    </template>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
          <v-menu v-if="selected_page === 'portfolio'" offset-y left :close-on-content-click="false" :nudge-bottom="8">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="column-picker-button"
                icon
                color="black"
                :disabled="page_loading"
                aria-label="Choose visible columns"
                title="Visible columns"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon large>mdi-view-column-outline</v-icon>
              </v-btn>
            </template>
            <v-card class="column-picker-menu" width="300" rounded="lg" elevation="8">
              <div class="column-picker-heading">
                <div>
                  <div class="column-picker-title">Visible columns</div>
                  <div class="column-picker-subtitle">Customize your table view</div>
                </div>
                <v-btn
                  text
                  small
                  color="primary"
                  class="column-picker-reset"
                  :disabled="hidden_portfolio_columns.length === 0"
                  @click="hidden_portfolio_columns = []"
                >Show all</v-btn>
              </div>
              <v-divider></v-divider>
              <v-list dense class="column-picker-list">
                <v-list-item-group v-model="selectedPortfolioColumns" multiple color="primary">
                  <div v-for="group in portfolioColumnGroups" :key="group.title" class="column-picker-group">
                    <v-subheader class="column-picker-group-title">{{ group.title }}</v-subheader>
                    <v-list-item
                      v-for="column in group.columns"
                      :key="column.value"
                      :value="column.value"
                      :disabled="column.disabled"
                      class="column-picker-option"
                    >
                      <template v-slot:default="{ active }">
                        <v-list-item-title>{{ column.text }}</v-list-item-title>
                        <v-icon small :color="active ? 'primary' : 'grey lighten-1'">
                          {{ active ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                        </v-icon>
                      </template>
                    </v-list-item>
                  </div>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-menu>
        </div>
      </v-col>
      <v-col cols="12" v-if="page_loading">
        <v-row
          class="ma-0 pa-0 full-size-row"
          align="center"
          justify="center"
        >
          <v-col cols="auto">
            <v-progress-circular
              color="black"
              size="100"
              indeterminate
            ></v-progress-circular>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="ma-0 pa-0" v-else-if="selected_page == 'portfolio'">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <div class="portfolio-audience-bar">
              <v-icon size="18" color="primary">mdi-account-group-outline</v-icon>
              <span class="portfolio-audience-bar__label">Plan Audience</span>
              <span class="portfolio-audience-bar__value" :title="sharedAudienceLabel">{{ sharedAudienceLabel }}</span>
              <span class="portfolio-audience-bar__scope">Applies to all line items</span>
              <v-btn
                small
                text
                color="primary"
                :disabled="page_loading || audienceSaving || true"
                aria-label="Change plan audience for all line items"
                @click="dialogAudience = true; audienceError = ''"
              >
                <v-icon left small>mdi-pencil-outline</v-icon>
                Change Audience
              </v-btn>
              <v-btn
                small
                text
                color="primary"
                :disabled="page_loading || audienceSaving || !sweetspot_data.some(component => component.audience) || true"
                aria-label="Use market population for all line items"
                @click="saveSharedAudience(null)"
              >
                <v-icon left small>mdi-restore</v-icon>
                Use Market Population
              </v-btn>
            </div>
            <DataLevelTable
              :tableHeader="visiblePortfolioHeaders"
              :tableData="portfolio_data"
              :selectedLevels="portfolioSelectedLevels"
              :combinedMarketsFirst="omni_channel_enabled"
              :numericColumns="['reach_pop', 'impressions', 'weeks', 'in_market_impressions', 'market_population', 'market_target_population']"
              :currencyColumns="['spend', 'cpm']"
              :decimalColumns="['avg_freq', 'grps', 'daily_grps']"
              :percentageColumns="['reach_pct', 'discount', 'IM', 'TGT']"
              :filterable="true"
              :data_filters="portfolioFilterVals"
              :filter_all_vals="portfolioFilterVals"
              :inlineEditConfig="inlineEditConfig"
              :rowEditable="true"
              :rowDeletable="true"
              :rowDuplicable="true"
              :actions_locked="false"
              :addNewRowEnabled="true"
              addNewRowText="Add New Component"
              v-on="dataLevelTableListeners"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        class="ma-0 pa-0 performance-panel"
        v-else-if="selected_page == 'performance'"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <SweetSpotReachChart
              chart_title="Reach Analysis"
              :chart_data="reach_chart_data"
              chart_id="reach_chart"
              color_palette="cool"
              v-on:update_data_from_marker="update_data_from_marker"
            >
            </SweetSpotReachChart>
          </v-col>
          <v-col cols="6">
            <ReachComp
              chart_title="Reach Comparison"
              :chart_data="reach_comp_chart_data"
              chart_id="reach_comp_chart"
              color_palette="cool"
              :frequency_axis_bottom="true"
              v-if="show_reach_comp_chart"
            >
            </ReachComp>
          </v-col>
          <v-col cols="6">
            <LineChart
              chart_title="Frequency Distribution"
              :chart_data="frequency_plot_data"
              chart_id="frequency_dist_chart"
              color_palette="cool"
              :chart_key="freq_plot_key"
              v-if="show_freq_chart"
            >
            </LineChart>
          </v-col>
        </v-row>
        <v-row class="ma-0 mb-2 pb-0">
          <v-col cols="12" class="ma-0 pa-0">
            <div class="pie-chart-toolbar">
              <v-btn-toggle
                v-model="selected_pie_metric"
                class="pie-metric-toggle"
                mandatory
                dense
                @change="update_pie_chart_data_from_marker"
              >
                <v-btn class="pie-metric-toggle__btn" small value="spend">Spend</v-btn>
                <v-btn class="pie-metric-toggle__btn" small value="impressions">Impressions</v-btn>
                <v-btn class="pie-metric-toggle__btn" small value="grps">GRPs</v-btn>
                <v-btn class="pie-metric-toggle__btn" small value="daily_grps">Daily GRPs</v-btn>
              </v-btn-toggle>
            </div>
            <div class="pie-chart-grid">
              <div
                class="pie-chart-grid__item"
                v-for="(value, chart) in pie_charts_data"
                :key="chart"
              >
                <SweetSpotPieChart
                  class="pie-chart-card"
                  :chart_title="value['title']"
                  :chart_data="value['data']"
                  color_palette="cool"
                >
                </SweetSpotPieChart>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        class="ma-0 pa-0 inventory-panel"
        v-else-if="selected_page == 'inventory'"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="12" v-if="inventory_loading">
            <v-row
              class="ma-0 pa-0 inventory-loading-state"
              align="center"
              justify="center"
            >
              <v-col cols="auto">
                <v-progress-circular
                  color="black"
                  size="100"
                  indeterminate
                ></v-progress-circular>
              </v-col>
            </v-row>
          </v-col>
          <template v-else>
            <v-col cols="12" class="ma-0 pa-0 inventory-map-column">
              <SweetSpotInventoryMap
                ref="inventoryMap"
                :locations="inventory_filtered_table_data"
                :mapHeight="inventory_table_visible ? 'clamp(280px, 40vh, 480px)' : 'clamp(400px, 65vh, 800px)'"
              />
            </v-col>
            <v-col
              v-show="inventory_table_visible"
              id="sweetspot-inventory-table"
              cols="12"
              class="ma-0 pa-0 inventory-table-column"
            >
              <DataLevelTable
                :tableHeader="inventoryHeaders"
                :tableData="inventory_filtered_table_data"
                :selectedLevels="inventorySelectedLevels"
                :filterBaseData="inventory_table_data"
                :numericColumns="['pixel_height', 'pixel_width', 'loop_length', 'fixed_spot_length', 'number_of_spots', 'impressions_weekly']"
                :decimalColumns="['latitude', 'longitude']"
                :currencyColumns="[]"
                :percentageColumns="['im']"
                :filterable="true"
                :data_filters="inventory_active_filters"
                :filter_all_vals="inventoryFilterVals"
                :showSelectedLevelValues="true"
                :rowEditable="false"
                :rowDeletable="false"
                :rowDuplicable="false"
                :locationIconStyles="inventoryLocationIconStyles"
                :actions_locked="true"
                :addNewRowEnabled="false"
                :super_short="true"
                tableHeight="clamp(220px, calc(100vh - 720px), 275px)"
                :reducedRowHeight="true"
                v-on:filter_data="filterInventoryData"
                v-on:locationClick="focusInventoryLocation"
              />
            </v-col>
          </template>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SweetSpotReachChart from "./SweetSpotComponents/SweetSpotCharts/SweetSpotReachChart.vue";
import LineChart from "@/components/ORT/Charts/LineChart.vue";
import DataLevelTable from "@/components/Utility/DataLevelTable.vue";
import SweetSpotPieChart from "./SweetSpotComponents/SweetSpotCharts/SweetSpotPieChart.vue";
import ReachComp from "@/components/ORT/Charts/ReachComp.vue";
import api_caller from "@/javascript/sweetspot_reporting.js";
import { audienceForPayload } from "@/javascript/sweetspot_audience.js";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, ALERT_GENERIC_MESSAGE } from "@/constants/constants";
import SweetSpotSetup from "./SweetSpotSetup.vue";
import SweetSpotOptimizeCard from "./SweetSpotComponents/SweetSpotOptimizeCard.vue";
import TooltipDescription from "@/components/Utility/TooltipDescription.vue";
import SweetSpotInventoryMap, { PRODUCT_STYLES } from "./SweetSpotComponents/SweetSpotInventoryMap.vue";

const DAYS_OF_WEEK = [
  { label: "Mon", value: "monday" },
  { label: "Tue", value: "tuesday" },
  { label: "Wed", value: "wednesday" },
  { label: "Thu", value: "thursday" },
  { label: "Fri", value: "friday" },
  { label: "Sat", value: "saturday" },
  { label: "Sun", value: "sunday" },
];

const DATA_LEVELS = [
  "market",
  "channel",
  "partner",
  "product",
  "format"
];

const INVENTORY_SELECTED_LEVELS = [
  "plan_name",
  "panel"
];

const PIE_METRIC_LABELS = {
  spend: "Spend",
  impressions: "Impressions",
  grps: "GRPs",
  daily_grps: "Daily GRPs",
};

const OMNI_CHANNEL_LEVELS = [
  "market",
  "partner",
  "channel",
  "product",
  "format"
];

const OMNI_CHANNEL_LEVEL_ORDER_REFRESH_DELAY_MS = 700;

export default {
  name: "SweetSpotOverview",
  props: ["report_id", "report_updated_time"],
  components: {
    SweetSpotSetup,
    SweetSpotOptimizeCard,
    TooltipDescription,
    DataLevelTable,
    SweetSpotReachChart,
    LineChart,
    SweetSpotPieChart,
    ReachComp,
    SweetSpotInventoryMap
  },
  data: () => ({
    selected_page: "portfolio",
    hidden_portfolio_columns: [],
    dialogCreate: false,
    dialogAudience: false,
    sharedAudience: null,
    audienceSaving: false,
    audienceError: "",
    pendingAudienceApplication: null,
    dialogComponentEdit: false,
    componentSubmissionMode: null,
    componentToEdit: null,
    dialogOptimize: false,
    optimization: {
      type: "efficient_reach",
      metric: "spend",
      target: null,
      offsets: [],
    },
    optimization_error: "",
    optimization_offset_id: 0,
    dialogEdit: false,
    active_edit_field: null,
    curr_row_edit_original: {},
    inline_edit_cell: null,
    inline_edit_value: "",
    inline_edit_error: "",

    page_loading: false,
    can_save_report: false,
    omni_channel_toggle_loading: false,

    portfolio_data: [],
    curr_component_spend: {},
    curr_row_edit_val: {},
    plan_idx_map: {},
    pie_charts_data: {},
    selected_pie_metric: "spend",

    reach_comp_chart_data: {},
    component_list: [],
    freq_plot_key: 0,
    show_freq_chart: true,
    show_reach_comp_chart: true,
    omni_channel_enabled: false,
    omni_channel_level_menu: false,
    omni_channel_level_order: [...OMNI_CHANNEL_LEVELS],
    omni_channel_level_order_refresh_timer: null,
    frequency_plot_data: [],
    sweetspot_data: [],
    fetch_report_request_id: 0,
    inventory_loading: false,
    inventory_table_visible: true,
    inventory_locations_by_plan: [],
    inventory_fetch_report_id: null,
    inventory_active_filters: {},
  }),

  mounted() {
    if (!this.report_id) {
      return;
    }

    this.fetch_report_data();
  },
  beforeDestroy() {
    this.clearScheduledOmniChannelLevelOrderRefresh();
  },
  watch: {
    visibleOmniChannelLevels(newLevels, oldLevels) {
      if (
        this.omni_channel_enabled &&
        newLevels.toString() !== oldLevels.toString()
      ) {
        this.scheduleOmniChannelLevelOrderRefresh();
      }
    },
    dialogEdit(newVal) {
      if (!newVal) {
        this.reset_edit_dialog_state();
      }
    },
    report_id(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      this.resetReportViewState();
      if (!newVal) {
        return;
      }

      this.fetch_report_data();
    },
    selected_page(newVal) {
      if (newVal === "inventory") {
        this.fetch_report_locations();
      }
    },
    "optimization.metric": function () {
      if (!this.dialogOptimize) {
        return;
      }

      this.setOptimizationTargetToCurrentMetricTotal();
    },
  },
  computed: {
    sharedAudienceLabel() {
      if (!this.sharedAudience) return this.sweetspot_data.some(component => component.audience) ? "Multiple audiences" : "Market population";
      if (this.sharedAudience.audience_name) return this.sharedAudience.audience_name;
      return this.sharedAudience.type === "existing_audience" ? "Saved audience" : "Custom audience";
    },
    visiblePortfolioHeaders() {
      return this.headers.filter((header) =>
        header.value === "actions" || !this.hidden_portfolio_columns.includes(header.value)
      );
    },
    selectedPortfolioColumns: {
      get() {
        return this.visiblePortfolioHeaders
          .filter((header) => header.value !== "actions")
          .map((header) => header.value);
      },
      set(columns) {
        if (columns.length === 0) {
          return;
        }
        this.hidden_portfolio_columns = this.headers
          .filter((header) => header.value !== "actions" && !columns.includes(header.value))
          .map((header) => header.value);
      },
    },
    portfolioColumnOptions() {
      const selected = this.selectedPortfolioColumns;
      return this.headers
        .filter((header) => header.value !== "actions")
        .map((header) => ({
          text: header.text,
          value: header.value,
          disabled: selected.length === 1 && selected.includes(header.value),
        }));
    },
    portfolioColumnGroups() {
      const groups = [
        { title: "Plan details", values: ["plan_name", ...OMNI_CHANNEL_LEVELS] },
        { title: "Audience & market", values: ["market_population", "market_target_population", "IM", "TGT"] },
        { title: "Delivery & schedule", values: ["impressions", "in_market_impressions", "grps", "daily_grps", "weeks", "schedule_summary"] },
        { title: "Reach & frequency", values: ["reach_pct", "reach_pop", "avg_freq"] },
        { title: "Pricing & spend", values: ["spend", "cpm", "discount"] },
      ];
      const groupedValues = new Set(groups.flatMap((group) => group.values));
      const remaining = this.portfolioColumnOptions.filter((column) => !groupedValues.has(column.value));
      const result = groups.map((group) => ({
        title: group.title,
        columns: this.portfolioColumnOptions.filter((column) => group.values.includes(column.value)),
      }));
      if (remaining.length) {
        result.push({ title: "Other", columns: remaining });
      }
      return result.filter((group) => group.columns.length > 0);
    },
    formattedReportUpdatedTime() {
      if (!this.report_updated_time) {
        return "";
      }

      const parsedDate = new Date(this.report_updated_time);
      if (Number.isNaN(parsedDate.getTime())) {
        return this.report_updated_time;
      }

      return parsedDate.toLocaleString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    saveTooltipTitle() {
      if (!this.formattedReportUpdatedTime) {
        return "Save Plan";
      }

      return `Save Plan | Last saved ${this.formattedReportUpdatedTime}`;
    },
    visibleOmniChannelLevels() {
      return this.omni_channel_level_order.filter((level) =>
        this.selectedPortfolioColumns.includes(level)
      );
    },
    portfolioSelectedLevels: function () {
      return this.omni_channel_enabled ? this.visibleOmniChannelLevels.concat(["plan_name"]) : [];
    },
    portfolioFilterVals: function () {
      const filter_vals = {};

      this.portfolioSelectedLevels.forEach((level) => {
        const values = [];

        (this.portfolio_data || []).forEach((row) => {
          if (row[level] != null && !values.includes(row[level])) {
            values.push(row[level]);
          }
        });

        filter_vals[level] = values;
      });

      return filter_vals;
    },
    dataLevelTableListeners: function () {
      return {
        editItem: this.editTableRow,
        start_edit_cell: this.startInlineEditCell,
        update_edit_value: this.updateInlineEditValue,
        save_edit_cell: this.saveInlineEditCell,
        cancel_edit_cell: this.resetInlineEditState,
        deleteItem: this.delete_report_component,
        duplicateItem: this.duplicate_report_component,
        add_new_row: this.openCreateDialog,
      };
    },
    inlineEditConfig: function () {
      return {
        columns: ["plan_name", "spend", "impressions", "grps", "daily_grps"],
        keyField: "plan_id",
        activeCell: this.inline_edit_cell,
        draftValue: this.inline_edit_value,
        error: this.inline_edit_error,
      };
    },
    inventorySelectedLevels: function () {
      return INVENTORY_SELECTED_LEVELS;
    },
    inventoryLocationIconStyles: function () {
      return PRODUCT_STYLES;
    },
    inventory_locations: function () {
      return this.inventory_filtered_table_data;
    },
    inventoryFilterVals: function () {
      const filter_vals = {};

      this.inventorySelectedLevels.forEach((level) => {
        const values = [];
        (this.inventory_table_data || []).forEach((row) => {
          if (row[level] != null && !values.includes(row[level])) {
            values.push(row[level]);
          }
        });
        filter_vals[level] = values;
      });
      return filter_vals;
    },
    inventoryHeaders: function () {
      return [
        { desc: "Plan Name", fixed: true, sortable: true, text: "Plan Name", value: "plan_name" },
        { desc: "Panel", fixed: true, sortable: true, text: "Panel", value: "panel" },
        { desc: "View location on map", fixed: true, sortable: false, text: "Location", value: "location" },
        { desc: "Market", fixed: true, sortable: true, text: "Market", value: "market" },
        { desc: "Province", fixed: true, sortable: true, text: "Province", value: "province" },
        { desc: "Channel", fixed: true, sortable: true, text: "Channel", value: "channel" },
        { desc: "Product", fixed: true, sortable: true, text: "Product", value: "product" },
        { desc: "Format", fixed: true, sortable: true, text: "Format", value: "format" },
        { desc: "Address", fixed: true, sortable: false, text: "Address", value: "address" },
        { desc: "Pixel Height", fixed: true, sortable: true, text: "Pixel Height", value: "pixel_height" },
        { desc: "Pixel Width", fixed: true, sortable: true, text: "Pixel Width", value: "pixel_width" },
        { desc: "Loop Length", fixed: true, sortable: true, text: "Loop Length", value: "loop_length" },
        { desc: "Fixed Spot Length", fixed: true, sortable: true, text: "Fixed Spot Length", value: "fixed_spot_length" },
        { desc: "Latitude", fixed: true, sortable: true, text: "Latitude", value: "latitude" },
        { desc: "Longitude", fixed: true, sortable: true, text: "Longitude", value: "longitude" },
        { desc: "Number of Spots", fixed: true, sortable: true, text: "Number of Spots", value: "number_of_spots" },
        { desc: "Weekly Impressions", fixed: true, sortable: true, text: "Weekly Impressions", value: "impressions_weekly" },
        { desc: "IM %", fixed: true, sortable: true, text: "In Market %", value: "im" },
      ];
    },
    inventory_table_data: function () {
      const rows = [];
      const rowKeys = new Set();
      (this.inventory_locations_by_plan || []).forEach((plan) => {
        (plan.locations || []).forEach((location) => {
          const row = {
            ...this.normalizeInventoryLocation(location),
            plan_id: plan.plan_id,
            plan_name: plan.plan_name,
          };
          const rowKey = [
            row.plan_id,
            row.panel,
            row.latitude,
            row.longitude,
            row.address,
            row.product,
            row.format,
          ].join("|");

          if (rowKeys.has(rowKey)) {
            return;
          }

          rowKeys.add(rowKey);
          rows.push(row);
        });
      });
      return rows;
    },
    inventory_filtered_table_data: function () {
      const filters = this.inventory_active_filters || {};
      const activeFilterKeys = Object.keys(filters).filter((key) => {
        return Array.isArray(filters[key]) && filters[key].length > 0;
      });

      if (activeFilterKeys.length === 0) {
        return this.inventory_table_data;
      }

      return this.inventory_table_data.filter((row) => {
        return activeFilterKeys.every((key) => {
          return row[key] == null || filters[key].includes(row[key]);
        });
      });
    },
    optimizationMetricOptions: function () {
      return [
        { text: "Spend", value: "spend" },
        { text: "Impressions", value: "impressions" },
        { text: "GRPs", value: "grps" },
        { text: "Daily GRPs", value: "daily_grps" },
      ];
    },
    optimizationTypeOptions: function () {
      return [
        { text: "Efficient Reach", value: "efficient_reach" },
        { text: "Volume Reach", value: "volume_reach" },
      ];
    },
    optimizationOffsetLevels: function () {
      return this.omniChannelLevelOptions;
    },
    omniChannelLevelOptions: function () {
      return OMNI_CHANNEL_LEVELS.map((level) => ({
        text: this.formatLevelLabel(level),
        value: level,
      }));
    },
    canRunOptimization: function () {
      const target = Number(this.optimization.target);
      return (
        this.sweetspot_data.length > 0 &&
        this.optimization.type &&
        this.optimization.metric &&
        Number.isFinite(target) &&
        target >= 0
      );
    },
    headers: function () {
      const levelHeaders = this.omni_channel_level_order.map((level) => ({
        desc: this.formatLevelLabel(level),
        fixed: true,
        sortable: ["market", "partner"].includes(level),
        text: this.formatLevelLabel(level),
        value: level,
      }));

      return [
        {
          desc: "Plan Name",
          fixed: true,
          sortable: true,
          text: "Plan Name",
          value: "plan_name",
        },
        ...levelHeaders,
        {
          desc: "Selected Audience",
          fixed: true,
          sortable: true,
          text: "Audience",
          value: "audience_name",
        },
        {
          desc: "Market Population",
          fixed: true,
          sortable: false,
          text: "Market Pop.",
          value: "market_population",
        },
        {
          desc: "Market Target Population",
          fixed: true,
          sortable: false,
          text: "Market Target Pop.",
          value: "market_target_population",
        },
        {
          desc: "Spend",
          fixed: true,
          sortable: false,
          text: "Spend",
          value: "spend",
        },
        {
          desc: "Impression",
          fixed: true,
          sortable: false,
          text: "Impressions",
          value: "impressions",
        },
        {
          desc: "In Market Impression",
          fixed: true,
          sortable: false,
          text: "In Market Impressions",
          value: "in_market_impressions",
        },
        {
          desc: "GRPs",
          fixed: true,
          sortable: false,
          text: "GRPs",
          value: "grps",
        },
        {
          desc: "Daily GRPs",
          fixed: true,
          sortable: false,
          text: "Daily GRPs",
          value: "daily_grps",
        },
        {
          desc: "Reach %",
          fixed: true,
          sortable: false,
          text: "Reach %",
          value: "reach_pct",
        },
        {
          desc: "Reach population",
          fixed: true,
          sortable: false,
          text: "Reach Population",
          value: "reach_pop",
        },
        {
          desc: "Average. Frequency",
          fixed: true,
          sortable: false,
          text: "Avg. Frequency",
          value: "avg_freq",
        },
        {
          desc: "Number of weeks in market",
          fixed: true,
          sortable: false,
          text: "Weeks",
          value: "weeks",
        },
        {
          desc: "Schedule Summary",
          fixed: true,
          sortable: false,
          text: "Schedule Summary",
          value: "schedule_summary",
        },
        {
          desc: "In Market %",
          fixed: true,
          sortable: false,
          text: "In Market %",
          value: "IM",
        },
        {
          desc: "Target %",
          fixed: true,
          sortable: false,
          text: "Target %",
          value: "TGT",
        },
        {
          desc: "CPM",
          fixed: true,
          sortable: false,
          text: "CPM",
          value: "cpm",
        },
        {
          desc: "Discount",
          fixed: true,
          sortable: false,
          text: "Discount",
          value: "discount",
        },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },
    reach_chart_data: function () {
      if (this.sweetspot_data.length == 0) {
        return [];
      }
      const component_data = [];
      this.sweetspot_data.forEach((component) => {
        const marker_idx = this.getSelectedComponentIndex(component);

        let reach_curve = [["Reach", "Reach Population", "Spend", "GRPs", "Daily GRPs", "Impressions"]];

        (component["table_stats"] || []).forEach((row) => {
          reach_curve.push([
            row["reach_pct"],
            row["reach_pop"],
            row["spend"],
            row["grps"],
            row["daily_grps"],
            row["impressions"],
          ]);
        });
        component_data.push({
          plan_name: component["plan_name"],
          component_id: component["component_id"],
          plan_id: component["plan_id"],
          reach_curve: reach_curve,
          marker_idx: marker_idx + 1, // add 1 to account for header row in reach curve data
        });
      });
      return component_data;
    },
  },
  methods: {
    closeAudienceDialog() {
      if (!this.audienceSaving) this.dialogAudience = false;
    },
    async saveSharedAudience(audience) {
      if (this.audienceSaving) return;
      audience = audienceForPayload(audience);
      const draftKey = JSON.stringify([this.report_id, audience]);
      if (this.pendingAudienceApplication?.draftKey === draftKey) {
        audience = this.pendingAudienceApplication.audience;
      }
      this.audienceSaving = true;
      this.audienceError = "";
      this.dialogAudience = false;
      this.set_report_loading(true);
      const requestedReportId = this.report_id;
      const requestedProjectId = this.$project.get_val();
      try {
        const token = await this.$auth.getTokenSilently();
        for (const component of this.sweetspot_data) {
          if (this.report_id !== requestedReportId) return;
          const response = await api_caller.edit_component(token, {
            project_id: requestedProjectId,
            report_id: requestedReportId,
            plan_id: component.plan_id,
            update_payload: {
              ...component,
              plan_name: this.getComponentPlanName(component),
              discount: component.discount ?? 0,
              selected_idx: this.getSelectedComponentIndex(component, false),
              event_schedule: component.event_schedule,
              audience,
            },
          });
          if (!response.success) throw new Error("Unable to update every line item. Retry to apply the audience to the entire plan.");
          if (['inputted-value', 'inputted_value'].includes(audience?.type)) {
            const data = response.data || {};
            const savedAudience = (data.edited_plan || data.updated_plan || data.component || data.plan)?.audience;
            if (savedAudience?.audience_size_id == null) {
              throw new Error("The audience was saved, but the API did not return its ID. Reload before applying it to the remaining line items.");
            }
            audience = audienceForPayload({
              ...savedAudience,
              audience_name: savedAudience.audience_name ?? audience.audience_name,
            });
            this.pendingAudienceApplication = { draftKey, audience };
          }
        }
        if (this.report_id !== requestedReportId) return;
        this.sharedAudience = JSON.parse(JSON.stringify(audience));
        this.pendingAudienceApplication = null;
        this.sweetspot_data = this.sweetspot_data.map(component => ({ ...component, audience: this.sharedAudience }));
        this.dialogAudience = false;
        if (this.sweetspot_data.length) await this.fetch_report_data(false);
      } catch (error) {
        this.audienceError = error.message || "Unable to save the plan audience. Please retry.";
        if (this.report_id === requestedReportId) this.dialogAudience = true;
      } finally {
        this.audienceSaving = false;
        this.set_report_loading(false);
      }
    },
    set_report_loading: function (value) {
      this.page_loading = value;
      this.$emit("report_loading_changed", value);
    },
    handleComponentSubmissionStarted: async function (mode) {
      this.componentSubmissionMode = mode;
      this.dialogCreate = false;
      this.dialogComponentEdit = false;
      this.set_report_loading(true);
      await this.$nextTick();
    },
    handleComponentSetupFailed: function () {
      this.componentSubmissionMode = null;
      this.set_report_loading(false);
      this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
    },
    setHasUnsavedCellEdits: function (value) {
      this.can_save_report = value;
      this.$emit("unsaved_cell_edits_changed", value);
    },
    resetMarketOmniChannels: function () {
      this.$emit("market_omni_channels_changed", {
        reach_pct: null,
        avg_freq: null,
        impressions: null,
        grps: null,
        spend: null,
      });
    },
    resetReportViewState: function () {
      this.pendingAudienceApplication = null;
      this.sharedAudience = null;
      this.dialogAudience = false;
      this.audienceError = "";
      this.selected_page = "portfolio";
      this.dialogCreate = false;
      this.dialogComponentEdit = false;
      this.componentToEdit = null;
      this.dialogOptimize = false;
      this.dialogEdit = false;
      this.optimization_error = "";
      this.optimization = {
        type: "efficient_reach",
        metric: "spend",
        target: null,
        offsets: [],
      };
      this.active_edit_field = null;
      this.curr_row_edit_original = {};
      this.inline_edit_cell = null;
      this.inline_edit_value = "";
      this.inline_edit_error = "";
      this.portfolio_data = [];
      this.curr_component_spend = {};
      this.curr_row_edit_val = {};
      this.plan_idx_map = {};
      this.pie_charts_data = {};
      this.reach_comp_chart_data = {};
      this.component_list = [];
      this.frequency_plot_data = [];
      this.sweetspot_data = [];
      this.inventory_loading = false;
      this.inventory_locations_by_plan = [];
      this.inventory_fetch_report_id = null;
      this.inventory_active_filters = {};
      this.omni_channel_enabled = false;
      this.omni_channel_level_menu = false;
      this.omni_channel_level_order = [...OMNI_CHANNEL_LEVELS];
      this.clearScheduledOmniChannelLevelOrderRefresh();
      this.setHasUnsavedCellEdits(false);
      this.resetMarketOmniChannels();
    },
    formatLevelLabel: function (level) {
      return String(level || "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    updateOmniChannelLevelOrder: async function (levels) {
      const nextOrder = this.normalizeOmniChannelLevelOrder(levels);

      if (nextOrder.toString() === this.omni_channel_level_order.toString()) {
        return;
      }

      this.omni_channel_level_order = nextOrder;

      if (this.omni_channel_enabled) {
        this.scheduleOmniChannelLevelOrderRefresh();
      }
    },
    scheduleOmniChannelLevelOrderRefresh: function () {
      this.clearScheduledOmniChannelLevelOrderRefresh();

      this.omni_channel_level_order_refresh_timer = setTimeout(async () => {
        this.omni_channel_level_order_refresh_timer = null;

        if (!this.omni_channel_enabled) {
          return;
        }

        this.omni_channel_toggle_loading = true;
        this.set_report_loading(true);
        await this.$nextTick();

        try {
          await this.update_omni_channel_rows();
        } finally {
          this.set_report_loading(false);
          this.omni_channel_toggle_loading = false;
        }
      }, OMNI_CHANNEL_LEVEL_ORDER_REFRESH_DELAY_MS);
    },
    clearScheduledOmniChannelLevelOrderRefresh: function () {
      if (!this.omni_channel_level_order_refresh_timer) {
        return;
      }

      clearTimeout(this.omni_channel_level_order_refresh_timer);
      this.omni_channel_level_order_refresh_timer = null;
    },
    moveOmniChannelLevel: async function (index, direction) {
      const targetIndex = index + direction;

      if (
        index <= 0 ||
        targetIndex <= 0 ||
        targetIndex >= this.omni_channel_level_order.length
      ) {
        return;
      }

      const nextOrder = [...this.omni_channel_level_order];
      const level = nextOrder[index];
      this.$set(nextOrder, index, nextOrder[targetIndex]);
      this.$set(nextOrder, targetIndex, level);

      await this.updateOmniChannelLevelOrder(nextOrder);
    },
    normalizeOmniChannelLevelOrder: function (levels) {
      const order = ["market"];

      (levels || []).forEach((level) => {
        if (
          level !== "market" &&
          OMNI_CHANNEL_LEVELS.includes(level) &&
          !order.includes(level)
        ) {
          order.push(level);
        }
      });

      OMNI_CHANNEL_LEVELS.forEach((level) => {
        if (!order.includes(level)) {
          order.push(level);
        }
      });

      return order;
    },
    openCreateDialog: function () {
      this.dialogCreate = true;
    },
    closeComponentEditDialog: function () {
      this.dialogComponentEdit = false;
      this.componentToEdit = null;
    },
    openOptimizeDialog: function () {
      this.optimization_error = "";
      this.setOptimizationTargetToCurrentMetricTotal();
      this.dialogOptimize = true;
    },
    updateOptimizationField: function ({ field, value }) {
      this.optimization = {
        ...this.optimization,
        [field]: value,
      };
    },
    setOptimizationTargetToCurrentMetricTotal: function () {
      const currentTotal = this.getCurrentPlanMetricTotal(this.optimization.metric);
      this.optimization = {
        ...this.optimization,
        target: currentTotal ? Math.round(currentTotal * 100) / 100 : null,
        offsets: this.optimization.offsets || [],
      };
    },
    getOptimizationOffsetValues: function (level) {
      const values = [];

      (this.sweetspot_data || []).forEach((component) => {
        const value = component[level];
        if (value != null && !values.includes(value)) {
          values.push(value);
        }
      });

      return values;
    },
    addOptimizationOffset: function () {
      this.optimization_offset_id += 1;
      this.optimization.offsets.push({
        id: this.optimization_offset_id,
        level: "market",
        value: null,
        offset: 0,
      });
    },
    removeOptimizationOffset: function (index) {
      this.optimization.offsets.splice(index, 1);
    },
    getCurrentPlanMetricTotal: function (metric) {
      return (this.sweetspot_data || []).reduce((total, component) => {
        const selected_idx = this.getSelectedComponentIndex(component);
        const value = Number(component["table_stats"]?.[selected_idx]?.[metric]);
        return total + (Number.isFinite(value) ? value : 0);
      }, 0);
    },
    getValidOptimizationOffsets: function () {
      return (this.optimization.offsets || [])
        .filter((offset) => {
          const amount = Number(offset.offset);
          return offset.level && offset.value != null && Number.isFinite(amount);
        })
        .map((offset) => ({
          level: offset.level,
          value: offset.value,
          offset: Number(offset.offset),
        }));
    },
    buildOptimizationShareTargets: function (metric, offsets) {
      const baseTotals = {};
      let baseTotal = 0;

      offsets.forEach((offset) => {
        const key = `${offset.level}::${offset.value}`;
        baseTotals[key] = 0;
      });

      (this.sweetspot_data || []).forEach((component) => {
        const selected_idx = this.getSelectedComponentIndex(component);
        const stats = component["table_stats"]?.[selected_idx] || {};
        const value = Number(stats[metric]) || 0;
        baseTotal += value;

        offsets.forEach((offset) => {
          if (component[offset.level] === offset.value) {
            const key = `${offset.level}::${offset.value}`;
            baseTotals[key] += value;
          }
        });
      });

      const targets = {};
      offsets.forEach((offset) => {
        const key = `${offset.level}::${offset.value}`;
        const baseShare = baseTotal > 0 ? baseTotals[key] / baseTotal : 0;
        const requestedShare = offset.offset / 100;

        targets[key] =
          requestedShare >= 0
            ? Math.min(1, requestedShare)
            : Math.max(0, baseShare * (1 + requestedShare));
      });

      return targets;
    },
    getOptimizationMetricValue: function (component, idx, metric) {
      const value = Number(component["table_stats"]?.[idx]?.[metric]);
      return Number.isFinite(value) ? value : 0;
    },
    getOptimizationReachValue: function (component, idx) {
      const stats = component["table_stats"]?.[idx] || {};
      const reach = Number(stats["reach_pop"]);

      if (Number.isFinite(reach)) {
        return reach;
      }

      const reachPct = Number(stats["reach_pct"]);
      return Number.isFinite(reachPct) ? reachPct : 0;
    },
    getComponentMaxMarginalEfficiency: function (component, metric) {
      let maxEfficiency = 0;
      const stats = component["table_stats"] || [];

      for (let idx = 1; idx < stats.length; idx++) {
        const deltaMetric =
          this.getOptimizationMetricValue(component, idx, metric) -
          this.getOptimizationMetricValue(component, idx - 1, metric);
        const deltaReach =
          this.getOptimizationReachValue(component, idx) -
          this.getOptimizationReachValue(component, idx - 1);

        if (deltaMetric > 0 && deltaReach > 0) {
          maxEfficiency = Math.max(maxEfficiency, deltaReach / deltaMetric);
        }
      }

      return maxEfficiency;
    },
    getComponentMaxMarginalReach: function (component) {
      let maxReach = 0;
      const stats = component["table_stats"] || [];

      for (let idx = 1; idx < stats.length; idx++) {
        const deltaReach =
          this.getOptimizationReachValue(component, idx) -
          this.getOptimizationReachValue(component, idx - 1);

        if (deltaReach > 0) {
          maxReach = Math.max(maxReach, deltaReach);
        }
      }

      return maxReach;
    },
    getOptimizationOffsetShareScore: function (
      component,
      deltaMetric,
      currentTotal,
      projectedTotal,
      offsets,
      shareTargets,
      groupTotals
    ) {
      if (projectedTotal <= 0) {
        return 0;
      }

      return offsets.reduce((score, offset) => {
        const key = `${offset.level}::${offset.value}`;
        const currentShare = currentTotal > 0 ? (groupTotals[key] || 0) / currentTotal : 0;
        const projectedGroupTotal =
          (groupTotals[key] || 0) +
          (component[offset.level] === offset.value ? deltaMetric : 0);
        const projectedShare = projectedGroupTotal / projectedTotal;
        const targetShare = shareTargets[key] || 0;

        if (offset.offset >= 0) {
          const currentDeficit = Math.max(0, targetShare - currentShare);
          const projectedDeficit = Math.max(0, targetShare - projectedShare);
          return score + (currentDeficit - projectedDeficit) * 10;
        }

        const currentSurplus = Math.max(0, currentShare - targetShare);
        const projectedSurplus = Math.max(0, projectedShare - targetShare);
        return score + (currentSurplus - projectedSurplus) * 10;
      }, 0);
    },
    scoreOptimizationCandidate: function (
      component,
      nextIdx,
      metric,
      optimizationType,
      state,
      offsets,
      shareTargets
    ) {
      const currentIdx = state.indices[component["plan_id"]] || 0;
      const deltaMetric =
        this.getOptimizationMetricValue(component, nextIdx, metric) -
        this.getOptimizationMetricValue(component, currentIdx, metric);
      const deltaReach =
        this.getOptimizationReachValue(component, nextIdx) -
        this.getOptimizationReachValue(component, currentIdx);

      if (deltaMetric <= 0) {
        return -Infinity;
      }

      const positiveDeltaReach = Math.max(0, deltaReach);
      const efficiency = positiveDeltaReach / deltaMetric;
      const maxEfficiency = state.maxEfficiency || efficiency || 1;
      const diminishingFactor = efficiency < maxEfficiency * 0.5 ? 0.45 : 1;
      const projectedTotal = state.total + deltaMetric;
      const offsetShareScore = this.getOptimizationOffsetShareScore(
        component,
        deltaMetric,
        state.total,
        projectedTotal,
        offsets,
        shareTargets,
        state.groupTotals
      );
      let offsetBias = 0;
      let offsetPenalty = 0;

      offsets.forEach((offset) => {
        const key = `${offset.level}::${offset.value}`;
        const currentShare = state.total > 0 ? (state.groupTotals[key] || 0) / state.total : 0;
        const projectedGroupTotal =
          (state.groupTotals[key] || 0) +
          (component[offset.level] === offset.value ? deltaMetric : 0);
        const projectedShare = projectedTotal > 0 ? projectedGroupTotal / projectedTotal : 0;
        const targetShare = shareTargets[key] || 0;
        offsetPenalty += Math.abs(projectedShare - targetShare);

        if (component[offset.level] === offset.value) {
          if (offset.offset >= 0 && targetShare > 0) {
            const shareDeficit = Math.max(0, targetShare - currentShare);
            offsetBias += (offset.offset / 20) * (shareDeficit / targetShare);
          } else if (offset.offset < 0) {
            const shareSurplus = Math.max(0, currentShare - targetShare);
            const scale = Math.max(currentShare, targetShare, 0.01);
            offsetBias -= (Math.abs(offset.offset) / 20) * (shareSurplus / scale);
          }
        }
      });

      if (optimizationType === "volume_reach") {
        const maxReachGain = state.maxReachGain || positiveDeltaReach || 1;
        return (
          (positiveDeltaReach / maxReachGain) * diminishingFactor +
          offsetBias +
          offsetShareScore -
          offsetPenalty * 0.15
        );
      }

      return (
        (efficiency / maxEfficiency) * diminishingFactor +
        offsetBias +
        offsetShareScore -
        offsetPenalty * 0.15
      );
    },
    optimizePlanIndices: function (metric, target, optimizationType, offsets) {
      const components = (this.sweetspot_data || []).filter(
        (component) => Array.isArray(component["table_stats"]) && component["table_stats"].length > 0
      );
      const shareTargets = this.buildOptimizationShareTargets(metric, offsets);
      const state = {
        total: 0,
        indices: {},
        groupTotals: {},
        maxEfficiency: 0,
        maxReachGain: 0,
      };

      offsets.forEach((offset) => {
        state.groupTotals[`${offset.level}::${offset.value}`] = 0;
      });

      components.forEach((component) => {
        const initialIdx = 0;
        const initialMetric = this.getOptimizationMetricValue(component, initialIdx, metric);

        state.indices[component["plan_id"]] = initialIdx;
        state.total += initialMetric;
        state.maxEfficiency = Math.max(
          state.maxEfficiency,
          this.getComponentMaxMarginalEfficiency(component, metric)
        );
        state.maxReachGain = Math.max(
          state.maxReachGain,
          this.getComponentMaxMarginalReach(component)
        );

        offsets.forEach((offset) => {
          if (component[offset.level] === offset.value) {
            const key = `${offset.level}::${offset.value}`;
            state.groupTotals[key] = (state.groupTotals[key] || 0) + initialMetric;
          }
        });
      });

      let guard = components.reduce(
        (total, component) => total + (component["table_stats"] || []).length,
        0
      );

      while (state.total < target && guard > 0) {
        guard -= 1;
        let best = null;

        components.forEach((component) => {
          const currentIdx = state.indices[component["plan_id"]] || 0;
          const nextIdx = currentIdx + 1;

          if (nextIdx >= component["table_stats"].length) {
            return;
          }

          const score = this.scoreOptimizationCandidate(
            component,
            nextIdx,
            metric,
            optimizationType,
            state,
            offsets,
            shareTargets
          );

          if (!Number.isFinite(score)) {
            return;
          }

          if (!best || score > best.score) {
            best = { component, nextIdx, score };
          }
        });

        if (!best) {
          break;
        }

        const component = best.component;
        const currentIdx = state.indices[component["plan_id"]] || 0;
        const deltaMetric =
          this.getOptimizationMetricValue(component, best.nextIdx, metric) -
          this.getOptimizationMetricValue(component, currentIdx, metric);
        const projectedTotal = state.total + deltaMetric;

        if (
          projectedTotal > target &&
          Math.abs(target - state.total) <= Math.abs(projectedTotal - target)
        ) {
          break;
        }

        state.indices[component["plan_id"]] = best.nextIdx;
        state.total = projectedTotal;

        offsets.forEach((offset) => {
          if (component[offset.level] === offset.value) {
            const key = `${offset.level}::${offset.value}`;
            state.groupTotals[key] = (state.groupTotals[key] || 0) + deltaMetric;
          }
        });
      }

      return state.indices;
    },
    runOptimization: function () {
      this.optimization_error = "";
      const target = Number(this.optimization.target);

      if (!this.canRunOptimization) {
        this.optimization_error = "Enter a non-negative optimization target.";
        return;
      }

      const offsets = this.getValidOptimizationOffsets();
      const optimizedIndices = this.optimizePlanIndices(
        this.optimization.metric,
        target,
        this.optimization.type,
        offsets
      );

      if (Object.keys(optimizedIndices).length === 0) {
        this.optimization_error = "No reach curves are available to optimize.";
        return;
      }

      this.disable_omni_channel_for_metric_change();
      this.resetMarketOmniChannels();
      this.plan_idx_map = {
        ...this.plan_idx_map,
        ...optimizedIndices,
      };
      this.refreshPortfolioViews();
      this.setHasUnsavedCellEdits(true);
      this.saveMetricChangesInBackground();
      this.dialogOptimize = false;
    },
    set_omni_channel_enabled: async function (enabled) {
      if (!enabled) {
        this.clearScheduledOmniChannelLevelOrderRefresh();
      }

      this.omni_channel_enabled = enabled;
      await this.update_omni_channel_rows();
    },
    disable_omni_channel_for_metric_change: function () {
      if (!this.omni_channel_enabled) {
        return;
      }

      this.clearScheduledOmniChannelLevelOrderRefresh();
      this.omni_channel_enabled = false;
      this.$emit("omni_channel_enabled_changed", false);
    },
    build_omni_channel: function () {
      this.set_omni_channel_enabled(!this.omni_channel_enabled);
    },
    handle_omni_channel_toggle: async function(enabled){
      this.omni_channel_enabled = enabled;

      if (!enabled) {
        await this.set_omni_channel_enabled(false);
        this.$emit("omni_channel_enabled_changed", false);
        return;
      }

      this.omni_channel_toggle_loading = true;
      this.set_report_loading(true);
      await this.$nextTick();

      try {
        if (this.can_save_report) {
          const saved = await this.save_portfolio_changes();

          if (!saved) {
            this.omni_channel_enabled = false;
            await this.set_omni_channel_enabled(false);
            this.$emit("omni_channel_enabled_changed", false);
            return;
          }
        }

        this.omni_channel_enabled = true;
        await this.set_omni_channel_enabled(true);
        this.$emit("omni_channel_enabled_changed", true);
      } finally {
        if (!this.omni_channel_enabled) {
          await this.set_omni_channel_enabled(false);
        }

        this.set_report_loading(false);
        this.omni_channel_toggle_loading = false;
      }
    },
    update_omni_channel_rows: async function () {
      const portfolio_rows = this.get_portfolio_rows_without_omni_channel();

      if (!this.omni_channel_enabled) {
        this.portfolio_data = portfolio_rows;
        return;
      }
      else{
        const omni_channel_rows = await this.build_omni_channel_rows(portfolio_rows);
        this.portfolio_data = (omni_channel_rows || []).concat(portfolio_rows);
      }
    },
    get_portfolio_rows_without_omni_channel: function () {
      return (this.portfolio_data || []).filter((row) => !row["is_omni_channel_row"]);
    },
    build_omni_channel_rows: async function () {
      const payload = {
        client_id: this.$project.get_val().slice(0, 4),
        report_id: this.report_id,
        level_order: this.visibleOmniChannelLevels,
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.calculate_omni_channel(token, payload);
        console.log(resp)

        if (resp["success"] ) {
          return resp['data']
        }
        else{
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        }
      } catch {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    normalizeInventoryNumber: function (value) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    },
    getInventoryValue: function (location, keys, fallback = "") {
      for (let key of keys) {
        if (location[key] != null) {
          return location[key];
        }
      }

      const normalizedKeyMap = {};
      Object.keys(location || {}).forEach((key) => {
        normalizedKeyMap[key.toLowerCase()] = key;
      });

      for (let key of keys) {
        const matchedKey = normalizedKeyMap[key.toLowerCase()];
        if (matchedKey && location[matchedKey] != null) {
          return location[matchedKey];
        }
      }

      return fallback;
    },
    normalizeInventoryLocation: function (location) {
      return {
        panel: this.getInventoryValue(location, ["panel", "panel_id"]),
        market: this.getInventoryValue(location, ["market"]),
        province: this.getInventoryValue(location, ["province"]),
        channel: this.getInventoryValue(location, ["channel", "super_type"]),
        product: this.getInventoryValue(location, ["product", "media_type"]),
        format: this.getInventoryValue(location, ["format", "product_type"]),
        address: this.getInventoryValue(location, ["address"]),
        pixel_height: this.normalizeInventoryNumber(this.getInventoryValue(location, ["pixel_height"], null)),
        pixel_width: this.normalizeInventoryNumber(this.getInventoryValue(location, ["pixel_width"], null)),
        loop_length: this.normalizeInventoryNumber(this.getInventoryValue(location, ["loop_length"], null)),
        fixed_spot_length: this.normalizeInventoryNumber(
          this.getInventoryValue(location, ["fixed_spot_length", "spot_length"], null)
        ),
        latitude: this.normalizeInventoryNumber(this.getInventoryValue(location, ["latitude", "lat"], null)),
        longitude: this.normalizeInventoryNumber(this.getInventoryValue(location, ["longitude", "long", "lng"], null)),
        number_of_spots: this.normalizeInventoryNumber(
          this.getInventoryValue(location, ["number_of_spots", "spots"], null)
        ),
        impressions_weekly: this.normalizeInventoryNumber(
          this.getInventoryValue(location, ["impressions_weekly", "weekly_impressions"], null)
        ),
        im: this.normalizeInventoryNumber(
          this.getInventoryValue(location, ["im"], null)
        ),
      };
    },
    getInventoryPlansFromResponse: function (resp) {
      const responseData = resp && resp["success"] ? resp["data"] : resp?.["data"] || resp;

      if (Array.isArray(responseData)) {
        return responseData;
      }

      return responseData?.["sweetspot_plan_locations"] || [];
    },
    focusInventoryLocation: function (location) {
      if (this.$refs.inventoryMap && this.$refs.inventoryMap.focusLocation) {
        this.$refs.inventoryMap.focusLocation(location);
      }
    },
    filterInventoryData: function (filters) {
      this.inventory_active_filters = filters || {};
    },
    fetch_report_locations: async function () {
      if (!this.report_id || this.inventory_loading) {
        return;
      }

      if (
        this.inventory_fetch_report_id === this.report_id &&
        this.inventory_locations_by_plan.length > 0
      ) {
        return;
      }

      this.inventory_loading = true;
      const requested_report_id = this.report_id;

      try {
        const payload = {
          client_id: this.$project.get_val().slice(0, 4),
          report_id: this.report_id,
        };
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.fetch_report_locations(token, payload);

        if (requested_report_id !== this.report_id) {
          return;
        }
        if (resp && (resp["success"] || resp["data"])) {
          this.inventory_locations_by_plan = this.getInventoryPlansFromResponse(resp);
          this.inventory_fetch_report_id = this.report_id;
          this.inventory_active_filters = {};
        } else {
          this.inventory_locations_by_plan = [];
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        }
      } catch {
        if (requested_report_id === this.report_id) {
          this.inventory_locations_by_plan = [];
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        }
      } finally {
        if (requested_report_id === this.report_id) {
          this.inventory_loading = false;
        }
      }
    },

    // Returns the display name for a plan from the backend-provided plan_name field.
    getComponentPlanName(component) {
      return component ? component["plan_name"] : "";
    },
    // Resolves a plan's selected table row index and clamps it to available stats.
    getSelectedComponentIndex(component, useMappedIndex = true) {
      if (
        !component ||
        !Array.isArray(component["table_stats"]) ||
        component["table_stats"].length === 0
      ) {
        return 0;
      }

      const planId = component["plan_id"];
      const mappedIndex = Number(this.plan_idx_map[planId]);
      const selectedIndex = useMappedIndex && Number.isInteger(mappedIndex)
        ? mappedIndex
        : Number(component["selected_idx"]);

      if (!Number.isInteger(selectedIndex) || selectedIndex < 0) {
        return 0;
      }

      return Math.min(selectedIndex, component["table_stats"].length - 1);
    },
    // Rebuilds chart component labels from the current plan list.
    rebuildComponentList: function () {
      this.component_list = this.sweetspot_data.map((component) => ({
        plan_name: this.getComponentPlanName(component),
        id: component["plan_id"],
      }));
    },
    formatDaySummary(dayValues) {
      return DAYS_OF_WEEK
        .filter((day) => dayValues.includes(day.value))
        .map((day) => day.label)
        .join(", ");
    },
    formatTimeSlotsSummary(timeSlots) {
      if (!timeSlots || timeSlots.length === 0) {
        return "No time slots";
      }

      return timeSlots.map((slot) => `${slot.start}-${slot.end}`).join(", ");
    },
    getEventScheduleSummary(eventSchedule) {
      if (
        !eventSchedule ||
        !eventSchedule["weekday_schedules"] ||
        eventSchedule["mode"] == null
      ) {
        return "";
      }

      if (eventSchedule["mode"] === "default") {
        return "Default schedule: Mon, Tue, Wed, Thu, Fri, Sat, Sun, 00:00-23:59";
      }

      if (eventSchedule["mode"] === "same_every_day") {
        const activeDays = DAYS_OF_WEEK
          .filter((day) => eventSchedule["weekday_schedules"][day.value]?.enabled)
          .map((day) => day.value);

        if (activeDays.length === 0) {
          return "";
        }

        const representativeDaySchedule =
          eventSchedule["weekday_schedules"][DAYS_OF_WEEK[0].value];

        return `${this.formatDaySummary(activeDays)}, ${this.formatTimeSlotsSummary(
          representativeDaySchedule?.time_slots || []
        )}`;
      }

      const enabledDaySummaries = DAYS_OF_WEEK.filter((day) => {
        return eventSchedule["weekday_schedules"][day.value]?.enabled;
      }).map((day) => {
        const schedule = eventSchedule["weekday_schedules"][day.value];

        return `${day.label}: ${this.formatTimeSlotsSummary(schedule.time_slots)}`;
      });

      return enabledDaySummaries.join(" | ");
    },
    fetch_report_data: async function (show_loading = true) {
      const request_id = this.fetch_report_request_id + 1;
      this.fetch_report_request_id = request_id;
      const requested_report_id = this.report_id;

      if (show_loading) {
        this.resetMarketOmniChannels();
        this.set_report_loading(true);
      }

      try {
        let payload = {
          client_id: this.$project.get_val().slice(0, 4),
          report_id: this.report_id,
        };

        let token = await this.$auth.getTokenSilently();
        let result = await api_caller
          .fetch_report_data(token, payload)
          .then((resp) => {
            if (resp["success"]) {
              return resp["data"];
            }
            return false;
          })
          .catch(() => {
            return false;
          });

        if (
          request_id !== this.fetch_report_request_id ||
          requested_report_id !== this.report_id
        ) {
          return;
        }

        if (result) {
          this.$emit(
            "market_omni_channels_changed",
            result["market_omni_channels"] || {}
          );

          const sweetspotPlans = result["sweetspot_plans"] || [];
          const firstAudience = sweetspotPlans[0]?.audience || null;
          const audienceKey = audience => {
            if (!audience) return null;
            if (['inputted-value', 'inputted_value'].includes(audience.type)) {
              return JSON.stringify(['manual', audience.audience_name, audience.audience_size]);
            }
            return JSON.stringify(audienceForPayload(audience));
          };
          this.sharedAudience = sweetspotPlans.every(component =>
            audienceKey(component.audience) === audienceKey(firstAudience)
          ) ? firstAudience : null;
          const temp_component_list = [];
          const temp_plan_idx_map = {};
          const temp_curr_component_spend = {};
          sweetspotPlans.forEach((component) => {
            temp_plan_idx_map[component["plan_id"]] =
              this.getSelectedComponentIndex(component, false);
            temp_curr_component_spend[component["component_id"]] = 300;
            
            temp_component_list.push({
              plan_name: this.getComponentPlanName(component),
              id: component["plan_id"],
            });
          });
          this.component_list = temp_component_list;
          this.plan_idx_map = temp_plan_idx_map;
          this.curr_component_spend = temp_curr_component_spend;

          this.sweetspot_data = sweetspotPlans;
          if (this.sweetspot_data && this.sweetspot_data.length > 0) {
            this.generate_portfilio_data();
            this.generate_frequency_plot_data();
            this.generate_reach_comp_chart_data();
            this.generate_pie_charts_data();
          } else {
            this.portfolio_data = [];
            this.frequency_plot_data = [];
            this.reach_comp_chart_data = {};
            this.pie_charts_data = {};
          }
          this.setHasUnsavedCellEdits(false);
        } else {
          this.resetReportViewState();
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        }
      } catch {
        if (
          request_id !== this.fetch_report_request_id ||
          requested_report_id !== this.report_id
        ) {
          return;
        }

        this.resetReportViewState();
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      } finally {
        if (
          show_loading &&
          request_id === this.fetch_report_request_id &&
          requested_report_id === this.report_id
        ) {
          this.set_report_loading(false);
        }
      }
    },
    update_data_from_marker: function (marker_payload) {
      const plan_id = marker_payload["plan_id"];
      const selected_idx = marker_payload["selected_idx"];
      const metric_changed = selected_idx !== this.plan_idx_map[plan_id];

      if (metric_changed) {
        this.disable_omni_channel_for_metric_change();
        this.resetMarketOmniChannels();
      }

      const tmp_plan_idx_map = { ...this.plan_idx_map };

      tmp_plan_idx_map[plan_id] = selected_idx;
      this.plan_idx_map = tmp_plan_idx_map;

      this.refreshPortfolioViews();

      if (metric_changed) {
        this.setHasUnsavedCellEdits(true);
        this.saveMetricChangesInBackground();
      }
    },
    refreshPortfolioViews: function () {
      this.rebuildComponentList();
      this.generate_portfilio_data();
      this.generate_reach_comp_chart_data();
      this.update_pie_chart_data_from_marker();
      this.update_freq_plot_by_marker();
    },
    update_freq_plot_by_marker: function () {
      this.generate_frequency_plot_data();

      this.show_freq_chart = false;
      setTimeout(() => {
        this.show_freq_chart = true;
      }, 100);
      this.freq_plot_key += 1;
    },
    update_pie_chart_data_from_marker: function () {
      this.generate_pie_charts_data();
    },
    generate_portfilio_data: async function () {
      const portfolio_data = [];

      if (this.sweetspot_data && this.sweetspot_data.length > 0) {
        this.sweetspot_data.forEach((component) => {
          const selected_idx = this.getSelectedComponentIndex(component);
          const comp_stats = component["table_stats"]?.[selected_idx] || {};

          portfolio_data.push({
            component_id: component["component_id"],
            plan_id: component["plan_id"],
            selected_idx: selected_idx,
            plan_name: this.getComponentPlanName(component),
            audience_name: component.audience?.audience_name || "All audiences",
            market: component["market"],
            partner: component["partner"],
            channel: component["channel"],
            product: component["product"],
            format: component["format"],
            weeks: component["weeks"],
            cpm: component["cpm"],
            discount: component["discount"],
            spend: comp_stats["spend"],
            impressions: comp_stats["impressions"],
            grps: comp_stats["grps"],
            daily_grps: comp_stats["daily_grps"],
            reach_pct: comp_stats["reach_pct"],
            reach_pop: comp_stats["reach_pop"],
            avg_freq: comp_stats["avg_freq"],
            
            in_market_impressions: comp_stats["in_market_impressions"],
            IM: comp_stats["IM"],
            TGT: comp_stats["TGT"],

            market_population: component["market_population"],
            market_target_population: component["market_target_population"],

            schedule_summary: this.getEventScheduleSummary(component["event_schedule"]),
          });
        });
      }
      if (this.omni_channel_enabled) {
        const omni_channel_rows = await this.build_omni_channel_rows();
        this.portfolio_data = (omni_channel_rows || []).concat(portfolio_data);
      } else {
        this.portfolio_data = portfolio_data;
      }
    },

    getFrequencyDistributionForSpend: function (component, spend) {
      const distribution = component["frequency_distributions"]?.[spend];

      if (Array.isArray(distribution)) {
        return {
          x: distribution.map((_, index) => index),
          y: distribution,
        };
      }

      if (
        distribution &&
        Array.isArray(distribution["x"]) &&
        Array.isArray(distribution["y"])
      ) {
        return {
          x: distribution["x"],
          y: distribution["y"],
        };
      }

      return {
        x: [],
        y: [],
      };
    },
    generate_frequency_plot_data: function () {
      const freq_chat_data = [
        ["Frequency"].concat(this.component_list.map((c) => c["plan_name"])),
      ];

      const temp_freq_dist_data = [];
      const frequency_values = [];
      this.sweetspot_data.forEach((component) => {
        let selected_idx = this.getSelectedComponentIndex(component);
        let comp_stats = component["table_stats"]?.[selected_idx] || {};
        let dist = this.getFrequencyDistributionForSpend(component, comp_stats["spend"]);

        dist["x"].forEach((value) => {
          if (!frequency_values.includes(value)) {
            frequency_values.push(value);
          }
        });
        temp_freq_dist_data.push(dist);
      });

      frequency_values.sort((a, b) => Number(a) - Number(b));

      frequency_values.forEach((frequency) => {
        let new_row = [frequency];
        for (let j = 0; j < temp_freq_dist_data.length; j++) {
          const dist_idx = temp_freq_dist_data[j]["x"].indexOf(frequency);
          if(dist_idx >= 0){
            new_row.push(temp_freq_dist_data[j]["y"][dist_idx]);
          }
          else{
            new_row.push(null)
          }
        }
        freq_chat_data.push(new_row);
      });

      this.frequency_plot_data = freq_chat_data;

      this.show_freq_chart = false;
      setTimeout(() => {
        this.show_freq_chart = true;
      }, 100);
    },
    generate_pie_charts_data: function () {
      const selectedMetric = this.selected_pie_metric || "spend";
      const selectedMetricLabel = PIE_METRIC_LABELS[selectedMetric] || selectedMetric;
      const formatMetricValue = (value) => {
        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) {
          return "N/A";
        }

        if (selectedMetric === "spend") {
          return `$${numericValue.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          })}`;
        }

        return numericValue.toLocaleString("en-US", {
          maximumFractionDigits: selectedMetric === "impressions" ? 0 : 2,
        });
      };
      const data_levels_set = {};
      DATA_LEVELS.forEach((level) => {
        data_levels_set[level] = {};

        this.sweetspot_data.forEach((component) => {
          const selected_idx = this.getSelectedComponentIndex(component);
          const comp_stats = component["table_stats"]?.[selected_idx] || {};
          const metric_value = Number(comp_stats[selectedMetric]) || 0;

          if (component[level] in data_levels_set[level]) {
            data_levels_set[level][component[level]] += metric_value;
          } else {
            data_levels_set[level][component[level]] = metric_value;
          }
        });
      });

      const pie_charts_data = {};

      for (const data_level in data_levels_set) {
        pie_charts_data[data_level] = {
          title: `${data_level.charAt(0).toUpperCase() + data_level.slice(1)}`,
          data: [],
        };
        for (const category in data_levels_set[data_level]) {
          pie_charts_data[data_level]["data"].push({
            name: category,
            value: data_levels_set[data_level][category],
            tooltip: `${category}<br/>${selectedMetricLabel}: ${formatMetricValue(data_levels_set[data_level][category])}`,
          });
        }
      }
      this.pie_charts_data = pie_charts_data;
      
    },
    generate_reach_comp_chart_data: function () {
      const reach_comp_chart_data = {
        lbls: [],
        rch: [],
        frq: [],
      };

      this.sweetspot_data.forEach((component) => {
        let selected_idx = this.getSelectedComponentIndex(component);
        let comp_stats = component["table_stats"]?.[selected_idx] || {};

        reach_comp_chart_data["lbls"].push(this.getComponentPlanName(component));
        reach_comp_chart_data["frq"].push(comp_stats['avg_freq']);
        reach_comp_chart_data["rch"].push(comp_stats['reach_pop']);
      });

      this.reach_comp_chart_data = reach_comp_chart_data;

      this.show_reach_comp_chart = false;
      setTimeout(() => {
        this.show_reach_comp_chart = true;
      }, 100);
    },
    componentNeedsReportHydration: function (component) {
      if (!component || !Array.isArray(component["table_stats"])) {
        return true;
      }

      const selected_idx = this.getSelectedComponentIndex(component, false);
      const comp_stats = component["table_stats"]?.[selected_idx] || {};

      const distribution = this.getFrequencyDistributionForSpend(
        component,
        comp_stats["spend"]
      );

      return distribution["y"].length === 0;
    },
    fetchHydratedReportComponent: async function (plan_id) {
      const payload = {
        client_id: this.$project.get_val().slice(0, 4),
        report_id: this.report_id,
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.fetch_report_data(token, payload);
        const report_data = resp["success"] ? resp["data"] || {} : {};
        const sweetspot_plans = resp["success"]
          ? report_data["sweetspot_plans"] || []
          : [];

        return {
          component:
            sweetspot_plans.find((component) => component["plan_id"] === plan_id) || null,
          report_data,
        };
      } catch {
        return null;
      }
    },
    refreshMarketOmniChannels: async function (report_data = null) {
      if (report_data) {
        this.$emit(
          "market_omni_channels_changed",
          report_data["market_omni_channels"] || {}
        );
        return;
      }

      const payload = {
        client_id: this.$project.get_val().slice(0, 4),
        report_id: this.report_id,
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.fetch_report_data(token, payload);

        if (resp["success"]) {
          this.$emit(
            "market_omni_channels_changed",
            resp["data"]?.["market_omni_channels"] || {}
          );
        }
      } catch {
        this.resetMarketOmniChannels();
      }
    },
    getCompleteComponentData: async function (new_component_data) {
      if (!this.componentNeedsReportHydration(new_component_data)) {
        return {
          component: new_component_data,
          report_data: null,
        };
      }

      const hydrated_result = await this.fetchHydratedReportComponent(
        new_component_data["plan_id"]
      );
      const hydrated_component = hydrated_result?.["component"] || null;

      if (!hydrated_component) {
        return {
          component: new_component_data,
          report_data: hydrated_result?.["report_data"] || null,
        };
      }

      return {
        component: {
          ...new_component_data,
          ...hydrated_component,
          report_updated_time: new_component_data["report_updated_time"],
        },
        report_data: hydrated_result?.["report_data"] || null,
      };
    },
    find_idx_closest_attribute_value: function (table_stats, attribute_key, current_value) {
      const numeric_value = Number(current_value);

      if (
        !Array.isArray(table_stats) ||
        table_stats.length === 0 ||
        !attribute_key ||
        Number.isNaN(numeric_value)
      ) {
        return -1;
      }

      const first_value = Number(table_stats[0][attribute_key]);
      const last_value = Number(table_stats[table_stats.length - 1][attribute_key]);

      if (numeric_value < first_value) {
        return 0;
      }

      if (numeric_value > last_value) {
        return table_stats.length - 1;
      }

      let left = 0;
      let right = table_stats.length - 1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const mid_value = Number(table_stats[mid][attribute_key]);

        if (mid_value === numeric_value) {
          return mid;
        }

        if (mid_value < numeric_value) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      const lower_idx = Math.max(0, right);
      const upper_idx = Math.min(table_stats.length - 1, left);
      const lower_value = Number(table_stats[lower_idx][attribute_key]);
      const upper_value = Number(table_stats[upper_idx][attribute_key]);

      if (Math.abs(numeric_value - lower_value) <= Math.abs(upper_value - numeric_value)) {
        return lower_idx;
      }

      return upper_idx;
    },
    lock_edit_field: function (field) {
      if (this.active_edit_field) {
        return;
      }

      if (this.curr_row_edit_val[field] !== this.curr_row_edit_original[field]) {
        this.active_edit_field = field;
      }
    },
    is_edit_field_disabled: function (field) {
      return this.active_edit_field != null && this.active_edit_field !== field;
    },
    reset_edit_dialog_state: function () {
      this.active_edit_field = null;
      this.curr_row_edit_original = {};
    },
    resetInventoryTable: function () {
      this.inventory_loading = false;
      this.inventory_locations_by_plan = [];
      this.inventory_fetch_report_id = null;
      this.inventory_active_filters = {};

      if (this.$refs.inventoryMap && this.$refs.inventoryMap.clearMarkerSelection) {
        this.$refs.inventoryMap.clearMarkerSelection();
      }
    },
    resetInlineEditState: function () {
      this.inline_edit_cell = null;
      this.inline_edit_value = "";
      this.inline_edit_error = "";
    },
    startInlineEditCell: function (payload) {
      const row_payload = payload["row"] || payload;
      this.inline_edit_cell = {
        rowKey: payload["rowKey"],
        column: payload["column"],
      };
      this.inline_edit_value =
        row_payload[payload["column"]] != null
          ? String(row_payload[payload["column"]])
          : "";
      this.inline_edit_error = "";
    },
    updateInlineEditValue: function (value) {
      this.inline_edit_value = value;

      if (!this.inline_edit_error) {
        return;
      }

      if (this.inline_edit_cell && this.inline_edit_cell["column"] === "plan_name") {
        if (String(value).trim() !== "") {
          this.inline_edit_error = "";
        }
        return;
      }

      const numeric_value = Number(value);
      if (String(value).trim() !== "" && !Number.isNaN(numeric_value) && numeric_value >= 0) {
        this.inline_edit_error = "";
      }
    },
    saveInlineEditCell: function (payload) {
      const row_payload = payload["row"] || payload;
      const raw_value = String(this.inline_edit_value).trim();

      if (payload["column"] === "plan_name") {
        if (raw_value === "") {
          this.inline_edit_error = "Enter a plan name";
          return;
        }

        const component = this.sweetspot_data.find(
          (entry) => entry["plan_id"] === row_payload["plan_id"]
        );

        if (!component || component["plan_name"] === raw_value) {
          this.resetInlineEditState();
          return;
        }

        this.$set(component, "plan_name", raw_value);
        this.refreshPortfolioViews();
        this.generate_reach_comp_chart_data();
        this.resetInventoryTable();
        this.setHasUnsavedCellEdits(true);
        this.resetInlineEditState();
        return;
      }

      const numeric_value = Number(raw_value);

      if (raw_value === "" || Number.isNaN(numeric_value) || numeric_value < 0) {
        this.inline_edit_error = "Enter a non-negative number";
        return;
      }

      const component = this.sweetspot_data.find(
        (entry) => entry["plan_id"] === row_payload["plan_id"]
      );

      if (!component) {
        this.resetInlineEditState();
        return;
      }

      const new_idx = this.find_idx_closest_attribute_value(
        component["table_stats"],
        payload["column"],
        numeric_value
      );

      if (new_idx < 0) {
        this.inline_edit_error = "No matching value available";
        return;
      }

      if (new_idx === this.plan_idx_map[component["plan_id"]]) {
        this.resetInlineEditState();
        return;
      }

      this.disable_omni_channel_for_metric_change();
      this.resetMarketOmniChannels();
      this.$set(this.plan_idx_map, component["plan_id"], new_idx);
      this.refreshPortfolioViews();
      this.resetInventoryTable();
      this.setHasUnsavedCellEdits(true);
      this.saveMetricChangesInBackground();
      this.resetInlineEditState();
    },

    editTableRow: function (payload) {
      const row_payload = payload["row"] || payload;
      this.resetInlineEditState();
      const component = this.sweetspot_data.find(
        (entry) => entry["plan_id"] === row_payload["plan_id"]
      );

      if (!component) {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      this.componentToEdit = {
        ...component,
        plan_name: this.getComponentPlanName(component),
      };
      this.dialogComponentEdit = true;
    },

    editTableRowConfirm: function () {
      const component = this.sweetspot_data.find(
        (entry) => entry["plan_id"] === this.curr_row_edit_val["plan_id"]
      );

      if (!component) {
        this.dialogEdit = false;
        return;
      }

      if (!this.active_edit_field) {
        this.reset_edit_dialog_state();
        this.dialogEdit = false;
        return;
      }

      const new_idx = this.find_idx_closest_attribute_value(
        component["table_stats"],
        this.active_edit_field,
        this.curr_row_edit_val[this.active_edit_field]
      );

      if (new_idx < 0) {
        this.reset_edit_dialog_state();
        this.dialogEdit = false;
        return;
      }

      const metric_changed = new_idx !== this.plan_idx_map[component["plan_id"]];

      if (metric_changed) {
        this.disable_omni_channel_for_metric_change();
        this.resetMarketOmniChannels();
      }

      this.plan_idx_map[component["plan_id"]] = new_idx;

      this.refreshPortfolioViews();

      if (metric_changed) {
        this.resetInventoryTable();
        this.setHasUnsavedCellEdits(true);
        this.saveMetricChangesInBackground();
      }

      this.reset_edit_dialog_state();
      this.dialogEdit = false;
    },
    save_portfolio_changes: async function (options = {}) {
      const show_success_alert = options["show_success_alert"] !== false;
      const portfolio_rows = this.portfolio_data
        .filter((row) => !row["is_omni_channel_row"])
        .map((row) => ({
          plan_id: row["plan_id"],
          plan_name: row["plan_name"],
          market: row["market"],
          partner: row["partner"],
          channel: row["channel"],
          product: row["product"],
          format: row["format"],
          weeks: row["weeks"],
          spend: row["spend"],
          grps: row["grps"],
          daily_grps: row["daily_grps"],
          impressions: row["impressions"],
          selected_idx: row["selected_idx"],
        }));

      const payload = {
        project_id: this.$project.get_val(),
        report_id: this.report_id,
        report_components: portfolio_rows,
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.save_report_plan(token, payload);

        if (resp["success"] && resp["data"] && resp["data"]["plan_ids"]) {
          const updated_time =
            resp["data"]["updated_time"] ||
            resp["data"]["updated_at"] ||
            resp["data"]["last_updated"] ||
            new Date().toISOString();

          this.setHasUnsavedCellEdits(false);
          this.$emit("report_saved", {
            updated_time,
            clear_unsaved_cell_edits: true,
          });
          if (show_success_alert) {
            this.$emit("triggerAlert", ALERT_TYPE_SUCCESS, "Portfolio saved successfully");
          }
          return true;
        }

        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return false;
      } catch {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return false;
      }
    },
    saveMetricChangesInBackground: async function () {
      const saved = await this.save_portfolio_changes({
        show_success_alert: false,
      });

      if (saved) {
        await this.fetch_report_data(false);
      }
    },
    delete_report_component: async function (component_data) {
      this.resetMarketOmniChannels();
      this.set_report_loading(true);
      await this.$nextTick();

      if (component_data["is_omni_channel_row"]) {
        try {
          this.portfolio_data = this.portfolio_data.filter((row) => row !== component_data);
        } finally {
          this.set_report_loading(false);
        }
        return;
      }

      try {
        const payload = {
          client_id: this.$project.get_val().slice(0, 4),
          plan_id: component_data["plan_id"],
        };

        let token = await this.$auth.getTokenSilently();
        let result = await api_caller
          .delete_report_component(token, payload)
          .then((resp) => {
            if (resp["success"]) {
              return resp["data"];
            }
            return false;
          })
          .catch(() => {
            return false;
          });

        if (!result) {
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
          return;
        }

        this.sweetspot_data = this.sweetspot_data.filter(
          (component) => component["plan_id"] !== component_data["plan_id"]
        );
        this.component_list = this.component_list.filter(
          (component) => component["id"] !== component_data["plan_id"]
        );
        this.resetInventoryTable();

        this.generate_portfilio_data();

        if (this.sweetspot_data.length > 0) {
          this.generate_frequency_plot_data();
          this.generate_reach_comp_chart_data();
          this.generate_pie_charts_data();
        } else {
          this.frequency_plot_data = [];
          this.reach_comp_chart_data = {};
          this.pie_charts_data = {};
        }

        this.$emit("report_saved", {
          updated_time:
            result["updated_time"] ||
            result["updated_at"] ||
            result["last_updated"] ||
            new Date().toISOString(),
          clear_unsaved_cell_edits: false,
        });
      } finally {
        this.set_report_loading(false);
      }

      await this.fetch_report_data(false);
    },
    duplicate_report_component: async function (component_data) {
      if (!component_data || component_data["is_omni_channel_row"]) {
        return;
      }

      const source_component = this.sweetspot_data.find(
        (component) => component["plan_id"] === component_data["plan_id"]
      );

      if (!source_component) {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
        return;
      }

      this.resetMarketOmniChannels();
      this.set_report_loading(true);
      await this.$nextTick();

      const source_plan_name = this.getComponentPlanName(source_component);
      const legacy_periods = { daily: 1, weekly: 7, monthly: 30 };
      const source_period = source_component["freq_cap_period"];
      const period_days = legacy_periods[source_period] || Number(source_period);
      const payload = {
        project_id: this.$project.get_val(),
        report_id: this.report_id,
        create_payload: {
          plan_name: `${source_plan_name} Copy`,
          market: source_component["market"],
          partner: source_component["partner"],
          channel: source_component["channel"],
          product: source_component["product"],
          format: source_component["format"],
          cpm: source_component["cpm"],
          discount: source_component["discount"] ?? 0,
          weeks: source_component["weeks"],
          freq_cap: /pattison/i.test(source_component["partner"] || "")
            ? 0
            : Number(source_component["freq_cap"] ?? 3),
          freq_cap_period: [1, 7, 30].includes(period_days) ? period_days : 1,
          event_schedule: source_component["event_schedule"] || [],
          audience: audienceForPayload(source_component.audience || this.sharedAudience) || { type: "relational_algebra", clauses: [], expression: "" },
        },
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const resp = await api_caller.add_component(token, payload);

        if (!resp["success"] || !resp["data"] || !resp["data"]["new_plan"]) {
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
          return;
        }

        const duplicated_component = resp["data"]["new_plan"];
        duplicated_component["plan_name"] =
          duplicated_component["plan_name"] || payload["create_payload"]["plan_name"];
        duplicated_component["event_schedule"] =
          duplicated_component["event_schedule"] || payload["create_payload"]["event_schedule"];
        duplicated_component["discount"] = payload["create_payload"]["discount"];
        duplicated_component["freq_cap"] = payload["create_payload"]["freq_cap"];
        duplicated_component["freq_cap_period"] = payload["create_payload"]["freq_cap_period"];
        duplicated_component["report_updated_time"] =
          resp["data"]["updated_time"] ||
          resp["data"]["updated_at"] ||
          resp["data"]["last_updated"] ||
          new Date().toISOString();

        await this.component_setup_completed(duplicated_component, {
          discount: payload["create_payload"]["discount"],
          freq_cap: payload["create_payload"]["freq_cap"],
          freq_cap_period: payload["create_payload"]["freq_cap_period"],
        });
      } catch {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      } finally {
        this.set_report_loading(false);
      }
    },
    component_setup_completed: async function (new_component_data, component_overrides = {}) {
      this.dialogCreate = false;
      this.componentSubmissionMode = null;
      this.set_report_loading(true);

      try {
        const complete_component_result =
          await this.getCompleteComponentData(new_component_data);
        const component_data = {
          ...complete_component_result["component"],
          ...component_overrides,
        };

        this.sweetspot_data = this.sweetspot_data.concat([component_data]);

        const tmp_curr_component_spend = { ...this.curr_component_spend };
        this.sweetspot_data.forEach((component) => {
          if (tmp_curr_component_spend[component["component_id"]] == null) {
            tmp_curr_component_spend[component["component_id"]] = 300;
          }
        });
        this.curr_component_spend = tmp_curr_component_spend;

        const tmp_plan_idx_map = { ...this.plan_idx_map };
        tmp_plan_idx_map[component_data["plan_id"]] =
          this.getSelectedComponentIndex(component_data);

        this.plan_idx_map = tmp_plan_idx_map;

        const temp_component_list = [];
        this.sweetspot_data.forEach((component) => {
          temp_component_list.push({
            plan_name: this.getComponentPlanName(component),
            id: component["plan_id"],
          });
        });
        this.component_list = temp_component_list;

        this.generate_portfilio_data();
        this.generate_frequency_plot_data();
        this.generate_reach_comp_chart_data();
        this.update_pie_chart_data_from_marker();
        this.resetInventoryTable();
        await this.refreshMarketOmniChannels(complete_component_result["report_data"]);

        this.$emit("report_saved", {
          updated_time:
            component_data["report_updated_time"] ||
            component_data["updated_time"] ||
            component_data["updated_at"] ||
            new Date().toISOString(),
          clear_unsaved_cell_edits: false,
        });
      } finally {
        this.set_report_loading(false);
      }
    },
    component_edit_completed: async function (updated_component_data) {
      this.closeComponentEditDialog();
      this.componentSubmissionMode = null;
      this.set_report_loading(true);

      try {
        const complete_component_result =
          await this.getCompleteComponentData(updated_component_data);
        const component_data = complete_component_result["component"];
        const component_index = this.sweetspot_data.findIndex(
          (component) => component["plan_id"] === component_data["plan_id"]
        );

        if (component_index < 0) {
          this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
          return;
        }

        this.$set(this.sweetspot_data, component_index, component_data);

        const tmp_curr_component_spend = { ...this.curr_component_spend };
        if (tmp_curr_component_spend[component_data["component_id"]] == null) {
          tmp_curr_component_spend[component_data["component_id"]] = 300;
        }
        this.curr_component_spend = tmp_curr_component_spend;

        const tmp_plan_idx_map = { ...this.plan_idx_map };
        tmp_plan_idx_map[component_data["plan_id"]] =
          this.getSelectedComponentIndex(component_data);
        this.plan_idx_map = tmp_plan_idx_map;

        this.refreshPortfolioViews();
        this.update_freq_plot_by_marker();
        this.resetInventoryTable();
        await this.refreshMarketOmniChannels(complete_component_result["report_data"]);

        this.$emit("report_saved", {
          updated_time:
            component_data["report_updated_time"] ||
            component_data["updated_time"] ||
            component_data["updated_at"] ||
            new Date().toISOString(),
          clear_unsaved_cell_edits: false,
        });
      } finally {
        this.set_report_loading(false);
      }
    },
  },
};
</script>

<style scoped>

.column-picker-button.v-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.column-picker-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  gap: 8px;
}

.column-picker-title {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
}

.column-picker-subtitle {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
}

.column-picker-reset.v-btn {
  min-width: auto;
  padding: 0 6px;
  letter-spacing: normal;
  text-transform: none;
}

.column-picker-list {
  max-height: 340px;
  overflow-y: auto;
  padding: 8px;
}

.column-picker-group + .column-picker-group {
  border-top: 1px solid #eef0f3;
  margin-top: 8px;
  padding-top: 4px;
}

.column-picker-group-title.v-subheader {
  height: 32px;
  padding: 0 12px;
  color: #667085;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.column-picker-option {
  border-radius: 6px;
  margin-bottom: 2px;
}


.report-container {
  max-width: 100%;
}

.full-size-row {
  height: 100%;
  width: 100%;
}

.performance-panel {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.inventory-loading-state {
  min-height: 360px;
}

.inventory-panel {
  max-height: calc(100vh - 246px);
  overflow-y: auto;
}

.inventory-map-column {
  padding-bottom: 10px !important;
}

.inventory-table-column .dataLevelTable {
  margin-top: 0;
}

.portfolio-audience-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 12px 4px;
  padding: 5px 12px;
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  background: #f3f4f7;
}

.portfolio-audience-bar__label,
.portfolio-audience-bar__scope {
  color: #707889;
  font-size: 11px;
}

.portfolio-audience-bar__value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #252d3d;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 600px) {
  .portfolio-audience-bar {
    flex-wrap: wrap;
    gap: 6px 10px;
  }

  .portfolio-audience-bar__scope {
    display: none;
  }
}

.report-toolbar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  justify-content: space-between;
  padding-bottom: 12px;
}

.report-toolbar__chips,
.report-toolbar__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  /* gap: 8px; */
}

.report-toolbar__chips {
  min-height: 40px;
}

.report-toolbar__actions {
  gap: 12px;
  justify-content: flex-end;
}

.save-tooltip-activator {
  display: inline-flex;
}

.omni-channel-toggle {
  align-items: center;
}

.omni-channel-toggle__label {
  font-family: "Poppins" !important;
  font-size: 0.85rem;
  font-weight: 500;
}

.omni-channel-level-button {
  min-width: 132px;
  padding-left: 12px !important;
  padding-right: 12px !important;
  text-transform: none;
}

.omni-channel-level-button__label {
  font-family: "Poppins" !important;
  font-size: 0.85rem;
  font-weight: 600;
}

.omni-channel-level-card {
  border-radius: 8px;
}

.omni-channel-level-card__title {
  font-family: Poppins;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 14px 16px 6px;
}

.omni-channel-level-card__content {
  padding: 6px 12px 12px !important;
}

.omni-channel-level-row {
  align-items: center;
  border-bottom: 1px solid #edf0f2;
  display: flex;
  justify-content: space-between;
  min-height: 44px;
  padding: 4px 0;
}

.omni-channel-level-row:last-child {
  border-bottom: 0;
}

.omni-channel-level-row--locked {
  opacity: 0.82;
}

.omni-channel-level-chip {
  font-family: Poppins;
  font-size: 0.85rem;
}

.omni-channel-level-row__actions {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  min-width: 72px;
}

.optimize-button {
  font-family: Poppins;
  font-weight: 600;
  text-transform: none;
}

.pie-chart-card {
  padding-bottom: 50px;
}

.pie-chart-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 24px;
}

.pie-metric-toggle {
  background: transparent !important;
  border-radius: 0;
  box-shadow: none;
  gap: 2px;
}

.pie-metric-toggle ::v-deep .pie-metric-toggle__btn {
  background-color: #ffffff !important;
  border: 2px solid #000000 !important;
  border-radius: 0 !important;
  color: #000000 !important;
  font-family: Poppins;
  font-weight: 600;
  justify-content: center;
  margin-left: 1px;
  margin-right: 1px;
  min-width: 125px;
  padding: 0 12px !important;
  text-transform: none;
}

.pie-metric-toggle ::v-deep .pie-metric-toggle__btn.v-btn--active {
  background-color: #000000 !important;
  color: #ffffff !important;
}

.pie-metric-toggle ::v-deep .pie-metric-toggle__btn::before {
  display: none;
}

.pie-chart-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, 1fr);
  gap: 16px;
  align-items: stretch;
  overflow-x: auto;
  padding-bottom: 8px;
}

.pie-chart-grid__item {
  min-width: 0;
}

.dialog_title,
.action_text {
  font-family: "Poppins";
}
</style>
